'use memo';

import type { DialogProps } from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import VStack from '@/components/Layout/VStack';
import { Text } from '@/components/Typography';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuthMethods } from '@/features/auth/hooks/useAuthMethods';
import { useReauthFlow } from '@/features/auth/hooks/useReauthFlow';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AuthMethodTabs } from './AuthMethodTabs';
type ReauthModalProps = DialogProps & {
  /**
   * モーダルが開かれた時に自動的にフローを初期化するかどうか
   * @default true
   */
  autoInit?: boolean;
  /**
   * 再認証成功後に実行するコールバック
   */
  onReauthSuccess?: () => Promise<void> | void;
};

/**
 * 再認証モーダルコンポーネント
 *
 * セッションの有効期限が切れた場合に、パスワード入力による再認証を行うモーダルです。
 * refresh=trueでログインフローを初期化し、パスワード入力フォームを表示します。
 */
export function ReauthModal(props: ReauthModalProps) {
  const {
    open,
    autoInit = true,
    onReauthSuccess,
    ...rest
  } = props;
  const handleOpenChange = (value: boolean) => {
    props.onOpenChange?.(value);
  };
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.reauth'
  });
  const {
    flow,
    isPending,
    isInitializing,
    onSubmit,
    initFlow
  } = useReauthFlow({
    enabled: open,
    reauthType: 'refresh',
    onSuccess: async () => {
      // 再認証成功時にコールバックを実行
      if (onReauthSuccess) {
        try {
          await onReauthSuccess();
        } catch (error) {
          console.error('Reauth success callback failed:', error);
          throw error;
        }
      }
      // 成功時にモーダルを閉じる
      handleOpenChange(false);
    }
  });
  const authMethods = useAuthMethods(flow?.ui ?? {
    nodes: [],
    messages: []
  });

  // モーダルが開かれた時にフローを初期化
  useEffect(() => {
    if (open && autoInit && !flow && !isInitializing) {
      initFlow();
    }
  }, [open, autoInit, flow, isInitializing, initFlow]);

  // フローが初期化されるまでローディング状態を表示
  if (!flow || isInitializing) {
    return <Dialog open={open} onOpenChange={handleOpenChange} {...rest}>
        <DialogContent className={cn('max-w-dialog-sm')}>
          <DialogHeader>
            <VStack className="items-center gap-3 mb-2">
              <Icon iconName="KeyRoundIcon" className="text-primary-on-neutral-fill animate-pulse" size={48} />
              <DialogTitle>{t('title')}</DialogTitle>
            </VStack>
            <DialogDescription asChild>
              <Text variant="p" className="text-neutral-muted-on-fill text-center">
                {t('description')}
              </Text>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>;
  }
  return <Dialog open={open} onOpenChange={handleOpenChange} {...rest}>
      <DialogContent className={cn('max-w-dialog-sm')}>
        <DialogHeader>
          <VStack className="items-center gap-3 mb-2">
            <Icon iconName="KeyRoundIcon" className="text-primary-on-neutral-fill" size={48} />
            <DialogTitle>{t('title')}</DialogTitle>
          </VStack>
          <DialogDescription asChild>
            <Text variant="p" className="text-neutral-muted-on-fill text-center">
              {t('description')}
            </Text>
          </DialogDescription>
        </DialogHeader>
        <VStack className="gap-5 py-3">
          <AuthMethodTabs methods={authMethods} disabled={isPending} onSubmit={onSubmit} />
        </VStack>
        <DialogFooter>
          <DialogClose asChild>
            <ActionButton variant="ghost" disabled={isPending}>
              {t('cancel')}
            </ActionButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}